import React, { useEffect, useState } from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Badge from '@amzn/awsui-components-console/badge';
import Box from '@amzn/awsui-components-console/box';
import Modal from '@amzn/awsui-components-console/modal';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Spinner from '@amzn/awsui-components-console/spinner';
import Table from '@amzn/awsui-components-console/table';
import { useListStringsLazyQuery } from 'src/generated/graphql';
import { MAX_SNAPSHOT_PAGES, SNAPSHOT_PAGE_SIZE } from '../constants';
import {
  StringGroupVersionSummary,
  StringInfo,
  TranslationStatus,
} from '../types';
import { formatDayMonthYear } from 'src/common/utils/dateFormatters';

interface ViewVersionModalProps {
  visible: boolean;
  version: number;
  namespaceId: string;
  stringGroupId: string;
  versionMeta?: StringGroupVersionSummary;
  onDismiss: () => void;
}

function statusBadgeColor(
  status: TranslationStatus,
): 'green' | 'blue' | 'red' | 'grey' {
  switch (status) {
    case TranslationStatus.Complete:
      return 'green';
    case TranslationStatus.InProgress:
      return 'blue';
    case TranslationStatus.Failed:
      return 'red';
    case TranslationStatus.Cancelled:
      return 'grey';
    // istanbul ignore next -- defensive fallback; all TranslationStatus values are handled above
    default:
      return 'grey';
  }
}

function formatDate(timestamp: number): string {
  // Missing server timestamps arrive as 0, which would otherwise render as
  // 01 Jan 1970 — match the em-dash placeholder the other panels use.
  if (!timestamp) {
    return '—';
  }
  return formatDayMonthYear(new Date(timestamp));
}

function mergeStrings(target: Map<string, StringInfo>, incoming: StringInfo[]) {
  for (const stringInfo of incoming) {
    const existing = target.get(stringInfo.stringId);
    if (!existing) {
      target.set(stringInfo.stringId, stringInfo);
      continue;
    }
    const variants = new Map(
      existing.variants.map((variant) => [variant.cxId, variant]),
    );
    stringInfo.variants.forEach((variant) =>
      variants.set(variant.cxId, variant),
    );
    target.set(stringInfo.stringId, {
      ...existing,
      ...stringInfo,
      variants: [...variants.values()],
    });
  }
}

const ViewVersionModal: React.FC<ViewVersionModalProps> = ({
  visible,
  version,
  namespaceId,
  stringGroupId,
  versionMeta,
  onDismiss,
}) => {
  const [fetchStrings] = useListStringsLazyQuery();
  const [strings, setStrings] = useState<StringInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!visible) return;

    // Drop the previous open's rows before the fetch starts. `loading` is only
    // set inside the async body below, so on the first render after reopening
    // for a different version the retained rows would otherwise show under the
    // new header for a frame. Clearing here also means `renderContent` never
    // shows one version's strings while another is being fetched.
    setStrings([]);
    setLoading(true);

    let cancelled = false;
    const loadSnapshot = async () => {
      setLoading(true);
      setError(null);
      const byStringId = new Map<string, StringInfo>();
      const seenPageTokens = new Set<string>();
      let pageCount = 0;
      let nextToken: string | null = null;

      try {
        do {
          pageCount += 1;
          if (pageCount > MAX_SNAPSHOT_PAGES) {
            throw new Error(
              'The version snapshot exceeded the pagination limit.',
            );
          }
          const result = await fetchStrings({
            variables: {
              namespaceId,
              stringGroupId,
              stringGroupVersion: version.toString(),
              maxResults: SNAPSHOT_PAGE_SIZE,
              ...(nextToken ? { nextToken } : {}),
            },
            fetchPolicy: 'network-only',
          });
          // Surface the real network/GraphQL error (useLazyQuery resolves with
          // data===undefined and error set on failure) rather than masking it
          // with the generic "did not contain data" message below.
          if (result.error) {
            throw result.error;
          }
          if (!result.data) {
            throw new Error('The snapshot response did not contain data.');
          }
          const page = result.data.listStrings;
          mergeStrings(
            byStringId,
            page.strings.map((stringInfo) => ({
              stringId: stringInfo.stringId,
              sourceLocale: stringInfo.sourceLocale,
              sourceText: stringInfo.sourceText,
              contextNote: stringInfo.contextNote ?? undefined,
              inlineMarkupType: stringInfo.inlineMarkupType ?? undefined,
              messageFormatType: stringInfo.messageFormatType ?? undefined,
              clientId: 'unknown',
              variants: stringInfo.variants.map((variant) => ({
                cxId: variant.cxId,
                sourceText: variant.sourceText,
                contextNote: variant.contextNote ?? undefined,
              })),
              translationStatus:
                (stringInfo.status as TranslationStatus | null) ??
                TranslationStatus.Complete,
            })),
          );
          const returnedNextToken = page.nextToken ?? null;
          // Guard against a backend that keeps returning the same token, which
          // would otherwise loop until MAX_SNAPSHOT_PAGES. Check membership
          // before adding — Set.add() returns the Set (always truthy), so it
          // cannot be used as the "already seen" signal.
          if (returnedNextToken && seenPageTokens.has(returnedNextToken)) {
            throw new Error(
              'The version snapshot returned a repeated page token.',
            );
          }
          if (returnedNextToken) {
            seenPageTokens.add(returnedNextToken);
          }
          nextToken = returnedNextToken;
        } while (nextToken);

        if (!cancelled) {
          setStrings(
            [...byStringId.values()].sort((left, right) =>
              left.stringId.localeCompare(right.stringId),
            ),
          );
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError instanceof Error
              ? loadError
              : new Error('Failed to load the version snapshot.'),
          );
          setStrings([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadSnapshot();
    return () => {
      cancelled = true;
    };
  }, [visible, version, namespaceId, stringGroupId, fetchStrings]);

  const renderContent = () => {
    if (loading) {
      return (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
          <Box variant="p" color="text-body-secondary" margin={{ top: 's' }}>
            Loading version strings…
          </Box>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert type="error">Failed to load strings: {error.message}</Alert>
      );
    }

    return (
      <SpaceBetween size="m">
        {versionMeta && (
          <Box color="text-body-secondary" fontSize="body-s">
            Snapshot from {formatDate(versionMeta.updatedOn)} &middot; by{' '}
            {versionMeta.updatedBy} &middot; {versionMeta.stringCount} strings
          </Box>
        )}

        <Table
          columnDefinitions={[
            {
              id: 'stringId',
              header: 'String ID',
              cell: (item: StringInfo) => (
                <Box fontWeight="bold">{item.stringId}</Box>
              ),
              width: 200,
            },
            {
              id: 'status',
              header: 'Status',
              cell: (item: StringInfo) => (
                <Badge color={statusBadgeColor(item.translationStatus)}>
                  {item.translationStatus}
                </Badge>
              ),
              width: 120,
            },
            {
              id: 'sourceText',
              header: 'Source Text',
              cell: (item: StringInfo) => item.sourceText,
            },
          ]}
          items={strings}
          stripedRows
          variant="embedded"
          empty={
            <Box textAlign="center" color="inherit" padding="l">
              No strings found for this version.
            </Box>
          }
        />
      </SpaceBetween>
    );
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="large"
      header={`View: Version v${version}`}
    >
      {renderContent()}
    </Modal>
  );
};

export default ViewVersionModal;
